if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.minimumOrderQuantity = Number.parseInt(this.dataset.minimumOrderQuantity || '1', 10);
        this.coolerStarterBundle = this.dataset.coolerStarterBundle === 'true';
        this.hideMonthlyCoolerOptions();
        this.setupDeliveryFrequency();
        this.setupCoolerPurchaseOptions();
        this.setupCoolerStarterBottles();
      }

      setupDeliveryFrequency() {
        this.deliveryFrequencyFieldset = this.form.querySelector('[data-water-delivery-frequency]');
        if (!this.deliveryFrequencyFieldset) return;

        this.deliveryFrequencyRadios = Array.from(
          this.deliveryFrequencyFieldset.querySelectorAll('[data-water-delivery-frequency-option]')
        );
        this.deliveryFrequencyError = this.deliveryFrequencyFieldset.querySelector(
          '[data-water-delivery-frequency-error]'
        );
        this.sellingPlanInput = this.form.querySelector('[data-selling-plan-input]');

        this.deliveryFrequencyRadios.forEach((radio) => {
          radio.addEventListener('change', () => this.updateDeliveryFrequency(radio));
        });
      }

      updateDeliveryFrequency(radio) {
        if (!radio.checked) return;

        this.deliveryFrequencyRadios.forEach((option) => option.setCustomValidity(''));
        this.deliveryFrequencyFieldset.classList.remove('water-delivery-frequency--error');
        if (this.deliveryFrequencyError) this.deliveryFrequencyError.hidden = true;

        if (!this.sellingPlanInput) return;
        const sellingPlanId = radio.dataset.sellingPlanId || '';
        this.sellingPlanInput.value = sellingPlanId;
        this.sellingPlanInput.disabled = sellingPlanId === '';
      }

      validateDeliveryFrequency() {
        if (!this.deliveryFrequencyFieldset) return true;

        const selectedFrequency = this.deliveryFrequencyRadios.find((radio) => radio.checked);
        if (selectedFrequency) {
          this.updateDeliveryFrequency(selectedFrequency);
          return true;
        }

        this.deliveryFrequencyFieldset.classList.add('water-delivery-frequency--error');
        if (this.deliveryFrequencyError) this.deliveryFrequencyError.hidden = false;

        const firstFrequency = this.deliveryFrequencyRadios[0];
        if (firstFrequency) {
          const validationMessage =
            this.deliveryFrequencyError?.textContent.trim() ||
            'Please select a water delivery frequency before adding this item to your cart.';
          firstFrequency.setCustomValidity(validationMessage);
          firstFrequency.reportValidity();
        }

        return false;
      }

      enforceMinimumOrderQuantity() {
        if (!Number.isFinite(this.minimumOrderQuantity) || this.minimumOrderQuantity <= 1) return;

        const quantityInput = this.form.elements.namedItem('quantity');
        if (!quantityInput || !('value' in quantityInput)) return;

        const requestedQuantity = Number.parseInt(quantityInput.value, 10);
        if (!Number.isFinite(requestedQuantity) || requestedQuantity < this.minimumOrderQuantity) {
          quantityInput.value = String(this.minimumOrderQuantity);
        }
      }

      setupCoolerPurchaseOptions() {
        if (!this.coolerStarterBundle) return;

        this.coolerSummaryTitle = this.form.querySelector('[data-cooler-summary-title]');
        this.coolerSummaryDetail = this.form.querySelector('[data-cooler-summary-detail]');
        this.coolerSellingPlanInput = this.form.querySelector('[data-cooler-selling-plan-input]');
        const configElement = this.form.querySelector('[data-cooler-variant-config]');

        try {
          this.coolerVariantConfig = JSON.parse(configElement?.textContent || '[]');
        } catch (error) {
          console.error('Unable to load cooler purchase options.', error);
          this.coolerVariantConfig = [];
        }

        this.variantIdInput.addEventListener('change', () => this.updateCoolerPurchaseOption());
        this.updateCoolerPurchaseOption();
      }

      updateCoolerPurchaseOption() {
        if (!this.coolerStarterBundle) return;

        const variant = this.coolerVariantConfig.find(
          (option) => String(option.id) === String(this.variantIdInput.value)
        );
        if (!variant) return;

        const variantTitle = String(variant.title || '').toLowerCase();
        const price = String(variant.price || '');
        const isYearlyRental = variantTitle.includes('year');

        if (isYearlyRental) {
          if (this.coolerSummaryTitle) this.coolerSummaryTitle.textContent = `Yearly rental — ${price} for 12 months`;
          if (this.coolerSummaryDetail) {
            this.coolerSummaryDetail.textContent =
              'This payment covers the first 12-month rental period. We’ll call after checkout to arrange delivery and confirm the rental details.';
          }
        } else {
          if (this.coolerSummaryTitle) this.coolerSummaryTitle.textContent = `Purchase outright — ${price}`;
          if (this.coolerSummaryDetail) {
            this.coolerSummaryDetail.textContent =
              'Own the cooler outright. We’ll call after checkout to arrange delivery.';
          }
        }

        if (this.coolerSellingPlanInput) {
          this.coolerSellingPlanInput.value = '';
          this.coolerSellingPlanInput.disabled = true;
        }
        this.toggleCoolerStarterBottles(isYearlyRental);
      }

      hideMonthlyCoolerOptions() {
        if (!this.coolerStarterBundle) return;

        const root = this.closest('product-info') || this.closest('section') || document;
        const hiddenMonthlyVariantIds = new Set(['43299557343310', '43299221471310']);
        if (hiddenMonthlyVariantIds.has(String(this.variantIdInput.value))) {
          const purchaseInput = Array.from(root.querySelectorAll('input[type="radio"][value]')).find((input) =>
            String(input.value).toLowerCase().includes('purchase')
          );
          if (purchaseInput) purchaseInput.click();
        }

        root.querySelectorAll('input[type="radio"][value]').forEach((input) => {
          if (!String(input.value).toLowerCase().includes('monthly')) return;
          const label = input.id ? root.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
          input.disabled = true;
          input.hidden = true;
          if (label) label.hidden = true;

          if (input.checked) {
            const replacementInput = Array.from(root.querySelectorAll(`input[type="radio"][name="${CSS.escape(input.name)}"]`))
              .find((option) => !option.disabled && !String(option.value).toLowerCase().includes('monthly'));
            if (replacementInput) replacementInput.click();
          }
        });

        root.querySelectorAll('select').forEach((select) => {
          const monthlyOptions = Array.from(select.options).filter((option) =>
            String(option.value || option.textContent).toLowerCase().includes('monthly')
          );
          monthlyOptions.forEach((option) => {
            option.disabled = true;
            option.hidden = true;
          });
          if (select.selectedOptions[0]?.disabled) {
            const replacementOption = Array.from(select.options).find((option) => !option.disabled);
            if (replacementOption) {
              select.value = replacementOption.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }

      toggleCoolerStarterBottles(isYearlyRental) {
        if (!this.coolerStarterFieldset) return;

        this.coolerStarterFieldset.hidden = !isYearlyRental;
        this.coolerStarterRadios.forEach((radio) => {
          radio.disabled = !isYearlyRental;
          radio.required = isYearlyRental;
          radio.setCustomValidity('');
          if (!isYearlyRental) radio.checked = false;
        });
        if (!isYearlyRental && this.coolerStarterError) this.coolerStarterError.hidden = true;
      }

      setupCoolerStarterBottles() {
        if (!this.coolerStarterBundle) return;

        this.coolerStarterFieldset = this.form.querySelector('[data-cooler-starter-bottles]');
        this.coolerStarterRadios = Array.from(this.form.querySelectorAll('[data-cooler-starter-variant]'));
        this.coolerStarterError = this.form.querySelector('[data-cooler-starter-error]');
        this.coolerStarterRadios.forEach((radio) => {
          radio.addEventListener('change', () => {
            this.coolerStarterRadios.forEach((option) => option.setCustomValidity(''));
            if (this.coolerStarterError) this.coolerStarterError.hidden = true;
          });
        });
        this.updateCoolerPurchaseOption();
      }

      validateCoolerStarterBottles() {
        if (!this.coolerStarterBundle) return true;

        const selectedCoolerVariant = this.coolerVariantConfig?.find(
          (option) => String(option.id) === String(this.variantIdInput.value)
        );
        const isYearlyRental = String(selectedCoolerVariant?.title || '').toLowerCase().includes('year');
        if (!isYearlyRental) return true;

        const selectedBottle = this.coolerStarterRadios.find((radio) => radio.checked);
        if (selectedBottle) return true;

        if (this.coolerStarterError) this.coolerStarterError.hidden = false;
        const firstBottle = this.coolerStarterRadios[0];
        if (firstBottle) {
          firstBottle.setCustomValidity('Please choose either the 11L or 15L returnable starter bottle.');
          firstBottle.reportValidity();
        }
        return false;
      }

      coolerBundlePayload(formData) {
        const selectedCoolerVariant = this.coolerVariantConfig?.find(
          (option) => String(option.id) === String(formData.get('id'))
        );
        const isYearlyRental = String(selectedCoolerVariant?.title || '').toLowerCase().includes('year');
        const selectedBottle = this.coolerStarterRadios.find((radio) => radio.checked);
        if (isYearlyRental && !selectedBottle) return null;

        const coolerProperties = {};
        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value) coolerProperties[propertyMatch[1]] = value;
        }
        if (isYearlyRental) {
          coolerProperties._cooler_starter_offer = 'Yearly rental includes 2 free returnable bottles; 6 starter bottles added';
        }

        const coolerItem = {
          id: Number(formData.get('id')),
          quantity: Number.parseInt(formData.get('quantity') || '1', 10),
          properties: coolerProperties,
        };

        const items = [coolerItem];
        if (isYearlyRental && selectedBottle) {
          items.push({
            id: Number(selectedBottle.value),
            quantity: 6,
            properties: {
              _cooler_starter_offer: '2 of 6 returnable bottles free with yearly cooler rental',
              _linked_cooler_variant: String(formData.get('id')),
            },
          });
        }

        return {
          items,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
          sections_url: window.location.pathname,
        };
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.enforceMinimumOrderQuantity();
        if (!this.validateDeliveryFrequency()) return;
        if (!this.validateCoolerStarterBottles()) return;
        if (!this.form.reportValidity()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (Number.isFinite(this.minimumOrderQuantity) && this.minimumOrderQuantity > 1) {
          const requestedQuantity = Number.parseInt(formData.get('quantity') || '0', 10);
          if (!Number.isFinite(requestedQuantity) || requestedQuantity < this.minimumOrderQuantity) {
            formData.set('quantity', String(this.minimumOrderQuantity));
          }
        }
        const smartBottleDealVariants = new Set(['41883802501198', '41883750989902']);
        if (smartBottleDealVariants.has(String(formData.get('id')))) {
          const paidQuantity = Number.parseInt(formData.get('quantity') || '0', 10);
          const freeQuantity = Number.isFinite(paidQuantity) ? Math.floor(paidQuantity / 10) : 0;
          if (freeQuantity > 0) {
            formData.set('quantity', String(paidQuantity + freeQuantity));
            formData.set('properties[Deal]', `Buy 10 Get One Free — ${freeQuantity} free bottle${freeQuantity === 1 ? '' : 's'} added automatically`);
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        if (this.coolerStarterBundle) {
          const coolerPayload = this.coolerBundlePayload(formData);
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(coolerPayload);
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
